import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import InfoOnlyBadge from '../../components/ui/InfoOnlyBadge';
import { isRealEmail } from '../../lib/email';
import { useAuth } from '../../lib/auth';
import { IconButton, PencilIcon, TrashIcon } from '../../components/ui/icons';
import { formatPhoneInput, isCompletePhone, displayPhone, telHref } from '../../lib/phone';

// Barn-wide directory of HORSE owners (clients). Barn owner/manager only.
// Note: these people hold the 'client' role, not 'owner' — 'owner' is the barn
// proprietor. Team management uses the same word for a very different thing.
// Add contacts here even before they own a horse; assign them to specific
// horses from the horse detail page or the owner's profile.
export default function OwnersDirectory() {
  const { activeRole } = useAuth();
  const canManage = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['owners'],
    queryFn: () => api.get('/owners'),
    enabled: canManage,
  });

  const remove = useMutation({
    mutationFn: (userId) => api.del(`/owners/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owners'] }),
  });

  if (!canManage) {
    return <div className="p-4 text-ink-500">The horse owners directory is for barn staff.</div>;
  }
  if (isLoading) return <div className="p-4 text-ink-400">Loading…</div>;
  if (isError) return <div className="p-4 text-red-600">Couldn’t load owners.</div>;

  const owners = data?.owners ?? [];

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Horse owners</h1>
        {!adding && (
          <button onClick={() => setAdding(true)} className="btn-primary">
            Add owner
          </button>
        )}
      </div>

      {adding && <AddOwnerForm onDone={() => setAdding(false)} />}

      {owners.length === 0 && !adding && (
        <p className="text-ink-400">
          No owners yet. Add one here, or assign owners from a horse’s page.
        </p>
      )}

      <ul className="space-y-2">
        {owners.map((o) => {
          const birthday = formatBirthday(o.birthday);
          const ownsHorses = o.horses.length > 0;
          return (
            <li key={o.userId} className="card p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Link
                  to={`/owners/${o.userId}`}
                  className="font-medium text-ink-900 hover:underline truncate block"
                >
                  {o.name || o.email}
                </Link>
                {/* Email and phone each get their own line: side by side they
                    truncated to nothing on a phone, and the number is the bit
                    you actually want to reach for. */}
                <div className="text-sm text-ink-500 break-words flex flex-wrap items-center gap-2">
                  {isRealEmail(o.email)
                    ? o.email
                    : <span className="text-ink-300">No email</span>}
                  {!isRealEmail(o.email) && <InfoOnlyBadge />}
                </div>
                {o.phone && <PhoneLine phone={o.phone} />}
                {birthday && <div className="text-sm text-ink-400">Birthday · {birthday}</div>}
                <div className="mt-1 text-sm">
                  {o.horses.length === 0 ? (
                    <span className="text-ink-400">No horses</span>
                  ) : (
                    o.horses.map((h, i) => (
                      <span key={h.id}>
                        {i > 0 && ', '}
                        <Link
                          to={`/horses/${h.id}`}
                          className="text-ink-600 hover:text-ink-900 underline"
                        >
                          {h.name}
                        </Link>
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <IconButton as={Link} to={`/owners/${o.userId}`} icon={PencilIcon} label="Edit" />
                {ownsHorses ? (
                  // Kept visible rather than hidden: the title is where the
                  // reason lives, and an action that silently isn't there is
                  // harder to understand than one that says why not.
                  <IconButton
                    icon={TrashIcon}
                    tone="muted"
                    disabled
                    label="Remove this owner from their horses before deleting"
                  />
                ) : (
                  <IconButton
                    icon={TrashIcon}
                    tone="danger"
                    label="Delete"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (confirm(`Remove ${o.name || o.email} from the directory?`)) {
                        remove.mutate(o.userId);
                      }
                    }}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// The number on its own line, as a tel: link — tap it on a handset and it
// offers to dial. Falls back to plain text if there's nothing dialable.
function PhoneLine({ phone }) {
  const href = telHref(phone);
  const shown = displayPhone(phone);
  if (!href) return <div className="text-sm text-ink-500">{shown}</div>;
  return (
    <div className="text-sm">
      <a href={href} className="text-ink-500 hover:text-ink-900 underline underline-offset-2">
        {shown}
      </a>
    </div>
  );
}

function AddOwnerForm({ onDone }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const add = useMutation({
    mutationFn: () =>
      api.post('/owners', {
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owners'] });
      onDone();
    },
  });

  const phoneOk = isCompletePhone(phone);
  const canSubmit = name.trim() && phoneOk && !add.isPending;

  return (
    <div className="card p-4 mb-4 space-y-2">
      <input
        className="input"
        placeholder="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className="input"
        type="email"
        placeholder="Email (optional)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <div>
        <input
          className="input"
          type="tel"
          inputMode="numeric"
          placeholder="Phone (optional)"
          value={phone}
          onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
        />
        {!phoneOk && <p className="mt-1 text-xs text-amber-600">Enter a 10-digit phone number.</p>}
      </div>

      {add.isError && <p className="text-sm text-red-600">{friendlyError(add.error)}</p>}

      <div className="flex gap-2 pt-1">
        <button onClick={() => add.mutate()} disabled={!canSubmit} className="btn-primary">
          {add.isPending ? 'Adding…' : 'Add Owner'}
        </button>
        <button onClick={onDone} className="text-sm text-ink-500 hover:text-ink-800 px-2">
          Cancel
        </button>
      </div>
    </div>
  );
}

// Format a stored ISO birthday (YYYY-MM-DD) as "June 15" — month name + day, no
// year. Parses the string directly to avoid Date() timezone shifts.
function formatBirthday(birthday) {
  if (!birthday) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthday);
  if (!m) return null;
  const monthIdx = Number(m[2]) - 1;
  const day = Number(m[3]);
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  if (monthIdx < 0 || monthIdx > 11 || !day) return null;
  return `${MONTHS[monthIdx]} ${day}`;
}

function friendlyError(err) {
  const code = err?.body?.error || err?.message || '';
  switch (code) {
    case 'missing_fields':
      return 'Name and email are both required.';
    case 'invalid_email':
      return 'That email does not look right.';
    case 'already_owner':
      return 'That person is already an owner in this barn.';
    case 'already_member':
      return 'That email already belongs to a staff member of this barn.';
    default:
      return 'Could not add owner. Try again.';
  }
}
